import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchProjects, fetchHomepageGallery, fetchSiteSettings, imgUrl, fullImgUrl, cardImage } from "../api";
import HeroGallery from "./HeroGallery";
import HeroLoader, { minLoaderDelay, maxLoaderDelay, preloadImages } from "./HeroLoader";
import CreditsBlock from "./CreditsBlock";
import AboutText from "./AboutText";
import TypewriterText from "./TypewriterText";
import VideoPlayer from "./VideoPlayer";
import "./HomePage.css";

// Cached images can already be complete before React attaches onLoad
const markLoaded = (el) => {
  if (el && el.complete) el.classList.add("loaded");
};

export default function HomePage() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomFullReady, setZoomFullReady] = useState(false);
  const [zoomClosing, setZoomClosing] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [aboutText, setAboutText] = useState("");
  const [loadProgress, setLoadProgress] = useState(0);
  const [loaderDone, setLoaderDone] = useState(false);
  const pendingGalleryRef = useRef([]);

  const [copied, setCopied] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  const ALL_TAGS = ["Video", "Image", "Graphic Design", "AI"];

  // Repeating rhythm for the project gallery: position + scale vary per item
  // so pages read less like a single column and more like a composed spread.
  const GALLERY_RHYTHM = [
    "alt-right alt-lg",
    "alt-left alt-sm",
    "alt-center alt-md",
    "alt-right alt-sm",
    "alt-left alt-lg",
    "alt-center alt-sm",
    "alt-right alt-md",
    "alt-left alt-md",
  ];

  // Position always comes from the rhythm; size can be pinned per-image from
  // the CMS (displayScale) when an image looks bad shown at its rhythm size.
  const galleryItemClass = (item, i) => {
    const [rhythmPos, rhythmSize] = GALLERY_RHYTHM[i % GALLERY_RHYTHM.length].split(" ");
    const scale = item.displayScale;
    const sizeClass = scale && scale !== "auto" ? `alt-${scale}` : rhythmSize;
    return `${rhythmPos} ${sizeClass}`;
  };

  const [transitionVisible, setTransitionVisible] = useState(false);
  const [transitionFading, setTransitionFading] = useState(false);
  const transitionTimeoutRef = useRef();
  const transitionHideRef = useRef();

  const projectContentRef = useRef(null);
  const mainScrollRef = useRef();
  const aboutSectionRef = useRef(null);
  const workSectionRef = useRef(null);
  const pendingScrollRef = useRef(null);

  const visibleProjects = projects.filter((proj) => proj.show);
  // The filter only narrows the homepage grid; the project-page index
  // always lists every project.
  const filteredProjects = visibleProjects.filter(
    (proj) => !activeFilter || proj.tags?.includes(activeFilter)
  );

  const resetScroll = useCallback(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, []);

  // Reveal gallery items as they scroll into view — no React state, no loops
  useEffect(() => {
    if (!activeProject) return;
    const scroll = mainScrollRef.current;
    if (!scroll) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { root: scroll, rootMargin: "120px" }
    );

    const rafId = requestAnimationFrame(() => {
      scroll.querySelectorAll(".gallery-alt-item").forEach((el) => observer.observe(el));
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [activeProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same reveal treatment for the homepage: project index items and about
  // text fade up as they enter the viewport.
  useEffect(() => {
    if (activeProject) return;
    const scroll = mainScrollRef.current;
    if (!scroll) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { root: scroll, rootMargin: "80px" }
    );

    const rafId = requestAnimationFrame(() => {
      scroll
        .querySelectorAll(".project-grid-item:not(.visible), .sidebar-about:not(.visible)")
        .forEach((el) => observer.observe(el));
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [activeProject, activeFilter, projects]);

  const closeZoom = useCallback(() => {
    setZoomClosing(true);
    setTimeout(() => {
      setZoomedImage(null);
      setZoomClosing(false);
    }, 280);
  }, []);

  // ESC closes the lightbox
  useEffect(() => {
    if (!zoomedImage) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomedImage, closeZoom]);

  useEffect(() => {
    const minDelay = minLoaderDelay();
    const maxDelay = maxLoaderDelay();

    async function load() {
      try {
        const [projectData, galleryData, settings] = await Promise.all([
          fetchProjects(),
          fetchHomepageGallery(),
          fetchSiteSettings(),
        ]);
        setProjects(projectData);
        setLoadProgress(0.2);

        // Hold the loader until the first gallery slides and the top of the
        // project grid have downloaded (same URLs the page renders, so they come
        // straight from cache), but never longer than MAX_LOADER_MS.
        const firstImages = [
          ...galleryData.slice(0, 2).map((item) => item.url && imgUrl(item.url, 2000)),
          ...projectData
            .filter((proj) => proj.show)
            .slice(0, 8)
            .map((proj) => imgUrl(cardImage(proj).url, 1200)),
        ];
        await Promise.race([
          Promise.all([
            preloadImages(firstImages, (p) => setLoadProgress(0.2 + 0.8 * p)),
            minDelay,
          ]),
          maxDelay,
        ]);
        setAboutText(settings.aboutText);
        // The gallery swaps in once the bar has visibly reached 100%
        pendingGalleryRef.current = galleryData;
        setLoaderDone(true);
      } catch (err) {
        console.error(err);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!projects.length) return;
    if (slug) {
      const project = projects.find((p) => p.slug === slug);
      setActiveProject(project || null);
      setZoomedImage(null);
      resetScroll();
      return;
    }
    setActiveProject(null);
    setZoomedImage(null);
    resetScroll();
  }, [projects, slug, resetScroll]);

  useEffect(() => {
    if (!activeProject && pendingScrollRef.current) {
      const target = pendingScrollRef.current;
      pendingScrollRef.current = null;
      setTimeout(() => {
        if (target === "about") aboutSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        if (target === "work") workSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
  }, [activeProject]);

  const triggerTransition = useCallback(() => {
    setTransitionVisible(true);
    setTransitionFading(false);
    clearTimeout(transitionTimeoutRef.current);
    clearTimeout(transitionHideRef.current);
    transitionTimeoutRef.current = setTimeout(() => setTransitionFading(true), 150);
    transitionHideRef.current = setTimeout(() => setTransitionVisible(false), 750);
  }, []);

  const handleProjectClick = useCallback(
    (proj) => {
      const target = typeof proj === "string"
        ? projects.find((p) => p.slug === proj)
        : proj;
      if (!target) return;
      triggerTransition();
      setTimeout(() => {
        setActiveProject(target);
        setZoomedImage(null);
        resetScroll();
        navigate(`/project/${target.slug}`);
      }, 80);
    },
    [projects, triggerTransition, resetScroll, navigate]
  );

  const handleWorkClick = useCallback(() => {
    if (activeProject) {
      triggerTransition();
      setTimeout(() => navigate("/"), 80);
    } else {
      workSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeProject, triggerTransition, navigate]);

  const handleAboutClick = useCallback(() => {
    if (activeProject) {
      pendingScrollRef.current = "about";
      triggerTransition();
      setTimeout(() => navigate("/"), 80);
    } else {
      aboutSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeProject, triggerTransition, navigate]);

  const handleNameClick = useCallback(() => {
    if (activeProject) {
      triggerTransition();
      setTimeout(() => navigate("/"), 80);
    } else {
      mainScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeProject, triggerTransition, navigate]);

  const handleImageClick = (url) => {
    setZoomClosing(false);
    setZoomedImage(url);
    // Open with the gallery-size image (already cached), swap in the
    // full-resolution original once it has finished downloading.
    setZoomFullReady(false);
    const full = new Image();
    full.onload = () => setZoomFullReady(true);
    full.src = fullImgUrl(url);
  };

  const handleVideoMeta = (e) => {
    const v = e.currentTarget;
    if (v.videoHeight > v.videoWidth) {
      v.style.maxHeight = '100vh';
      v.style.width = 'auto';
      v.style.maxWidth = '100%';
      if (v.closest('.alt-left')) v.style.marginLeft = 'auto';
      if (v.closest('.alt-center')) v.style.margin = '0 auto';
    }
  };

  const renderVideo = (url) => {
    if (!url) return null;
    const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
    if (isYouTube) {
      const videoId = url.includes("youtu.be/")
        ? url.split("youtu.be/")[1].split("?")[0]
        : new URL(url).searchParams.get("v");
      return (
        <div className="project-video-wrapper">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: "100%", border: "none", aspectRatio: "16/9" }}
          />
        </div>
      );
    }
    return (
      <div className="project-video-wrapper">
        <video src={url} controls className="project-video" preload="metadata" />
      </div>
    );
  };

  const renderGalleryItem = (item, index) => {
    if (item._type === "file" && item.videoUrl) {
      const showControls = !!activeProject?.galleryVideoControls;
      if (showControls) {
        return <VideoPlayer src={item.videoUrl} playsInline />;
      }
      return (
        <video
          src={item.videoUrl}
          className="project-image"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          webkit-playsinline="true"
          onLoadedMetadata={handleVideoMeta}
        />
      );
    }
    return (
      <img
        src={imgUrl(item.url, 2400)}
        alt=""
        className="project-image"
        width={item.width || undefined}
        height={item.height || undefined}
        loading={index < 2 ? "eager" : "lazy"}
        fetchPriority={index === 0 ? "high" : undefined}
        decoding="async"
        ref={markLoaded}
        onLoad={(e) => e.currentTarget.classList.add("loaded")}
        onClick={() => handleImageClick(item.url)}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const NavBar = (
    <div className="site-nav">
      {activeProject && (
        <div className="site-nav-back" onClick={handleNameClick} aria-label="Back to home">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </div>
      )}
      <div className="site-nav-links">
        <div className="nav-link" onClick={handleWorkClick}>
          Projects
        </div>
        <div className="nav-link" onClick={handleAboutClick}>
          About
        </div>
        <a
          className="nav-link"
          href="https://www.instagram.com/_leodubois"
          target="_blank"
          rel="noopener noreferrer"
        >
          Instagram
        </a>
        <div
          className={`nav-link contact-btn${copied ? " contact-copied" : ""}`}
          onClick={() => {
            navigator.clipboard.writeText("dubois.leo.design@gmail.com");
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          <span className="contact-label">Contact</span>
          <span className="contact-label-copied">Copied</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {NavBar}

      {transitionVisible && (
        <div className={`transition-overlay${transitionFading ? " hide" : ""}`} />
      )}

      <div className="main-scroll" ref={mainScrollRef}>
        {activeProject ? (
          <div className="project-detail-container">
            <div className="project-content-centered" key={activeProject._id} ref={projectContentRef}>
              <h2 className="project-title"><TypewriterText text={activeProject.title} charDelay={28} startDelay={0} /></h2>
              <p className="project-description"><TypewriterText text={activeProject.description} charDelay={8} startDelay={0} /></p>
              <CreditsBlock key={activeProject._id} credits={activeProject.credits} />
            </div>

            <div className="gallery-alternating">
              {[
                ...(activeProject.video1?.url ? [{ id: "__v1", _type: "__video", url: activeProject.video1.url }] : []),
                ...(activeProject.video2?.url ? [{ id: "__v2", _type: "__video", url: activeProject.video2.url }] : []),
                ...activeProject.gallery,
              ].map((item, i) => (
                <div
                  key={item.id}
                  className={`gallery-alt-item ${galleryItemClass(item, i)}`}
                >
                  {item._type === "__video"
                    ? renderVideo(item.url)
                    : renderGalleryItem(item, i)}
                </div>
              ))}
            </div>

            {zoomedImage && (
              <div
                className={`zoom-overlay${zoomClosing ? " closing" : ""}`}
                onClick={closeZoom}
              >
                <img
                  src={zoomFullReady ? fullImgUrl(zoomedImage) : imgUrl(zoomedImage, 2400)}
                  className="zoomed-image"
                  alt=""
                />
              </div>
            )}

            <div className="thumbnail-index-bar">
              {visibleProjects.map((proj) => (
                <img
                  key={proj._id}
                  src={imgUrl(
                    proj.thumbnail?.url ||
                      proj.gallery?.find((g) => g._type === "image")?.url ||
                      "",
                    300
                  )}
                  alt={proj.title}
                  className={`thumbnail-index-image ${activeProject._id === proj._id ? "active" : ""}`}
                  loading="lazy"
                  onClick={() => handleProjectClick(proj)}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <section className="homepage-gallery-section">
              {galleryItems.length > 0 ? (
                <HeroGallery
                  items={galleryItems}
                  onProjectClick={(slug) => handleProjectClick(slug)}
                />
              ) : (
                <HeroLoader
                  progress={loadProgress}
                  done={loaderDone}
                  onComplete={() => setGalleryItems(pendingGalleryRef.current)}
                />
              )}
            </section>

            <section className="homepage-about-section" ref={aboutSectionRef}>
              <AboutText className="sidebar-about" text={aboutText} />
            </section>

            <section className="homepage-work-section" ref={workSectionRef}>
              <div className="projects-filter-bar">
                <span className="projects-filter-label">Filter</span>
                {ALL_TAGS.map((tag) => (
                  <div
                    key={tag}
                    className={`nav-link${activeFilter === tag ? " active" : ""}`}
                    onClick={() => setActiveFilter(activeFilter === tag ? null : tag)}
                  >
                    {tag}
                  </div>
                ))}
              </div>
              <div className="projects-grid">
                {filteredProjects.map((proj) => (
                  <div
                    className="project-grid-item"
                    key={proj._id}
                    onClick={() => handleProjectClick(proj)}
                  >
                    <img
                      src={imgUrl(
                        proj.thumbnail?.url ||
                          proj.gallery?.find((g) => g._type === "image")?.url ||
                          "",
                        1200
                      )}
                      alt={proj.title}
                      className="project-grid-image"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="project-grid-title">{proj.title}</div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}
